import { ColumnSort, Filters, ORDER_VALUE, Range, users } from '@spo/logic';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { usePersonalOfferTable } from '../../hooks/usePersonalOfferTable';
import useSessionState from '../../hooks/useSessionState';
import { useUserConfig } from '../../hooks/useUserConfig';
import { Loading } from '../loading';
import { TableContainer, TablePagination } from '../table';
import { TableContainerHeader } from '../table/tableContainer/tableContainerHeader/TableContainerHeader';

export const MyView = ({
  openPosterModal,
}: {
  openPosterModal: () => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: userConfigData, isFetching: isUserConfigFetching } =
    useUserConfig();

  const [tabViewIndex, setTabViewIndex] = useSessionState<number>(
    'personal-offer_my-view_table-tab',
    0,
  );
  const [range, setRange] = useSessionState<Range>(
    'personal-offer_my-view_range',
    { start: 0, end: 9 },
  );
  const [currentPage, setCurrentPage] = useSessionState<number | null>(
    'personal-offer_my-view_current-page',
    1,
  );
  const [itemsPerPage, setItemsPerPage] = useSessionState<number>(
    'personal-offer_my-view_items-per-page',
    10,
  );
  const [columnSort, setColumnSort] = useSessionState<ColumnSort>(
    'personal-offer_my-view_column-sorting',
    {
      id: '',
      order: ORDER_VALUE.EMPTY,
    },
  );
  const [selectedFilters, setSelectedFilters] = useSessionState<Filters>(
    'personal-offer_my-view_selected-filters',
    {
      productManagerIds: [],
      productNatureIds: [],
      retailFamilyIds: [],
      segmentationIds: [],
      sportIds: [],
      status: [],
    },
  );

  const {
    data: superModelsTable,
    isFetching: isFetchingSM,
    isLoading,
    status: superModelsTableStatus,
  } = usePersonalOfferTable(userConfigData, range, columnSort, selectedFilters);

  useEffect(() => {
    if (userConfigData && !isUserConfigFetching) {
      if (userConfigData.needRedirect) {
        navigate('/personal-offer/my-config');
      } else {
        const urlWithParameters = users.buildUrlFromUserConfiguration(
          userConfigData,
          '/personal-offer/my-view',
        );
        navigate(urlWithParameters, { replace: true });
      }
    }
  }, [navigate, userConfigData, isUserConfigFetching]);

  useEffect(() => {
    if (currentPage) {
      const start = currentPage * itemsPerPage - itemsPerPage;
      const end = currentPage * itemsPerPage - 1;
      const stepIsZero = end - (itemsPerPage - 1) === start;
      const isRangeInSession = start === range.start && end === range.end;

      if (!isRangeInSession && start >= 0 && end > 0 && stepIsZero) {
        setRange({ start, end });
      }
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (!!currentPage && !!superModelsTable) {
      if (currentPage > superModelsTable.numberPages) {
        setCurrentPage(1);
      }
    }
  }, [superModelsTable?.numberPages]);

  return (
    <div data-testid="personal-offer">
      {superModelsTableStatus === 'pending' ||
      isLoading ||
      !superModelsTable?.data ? (
        <Loading />
      ) : (
        <div data-testid="table-container" className="table-container">
          <TableContainerHeader
            totalSMs={superModelsTable.totalSMs}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            tabViewIndex={tabViewIndex}
            setTabViewIndex={setTabViewIndex}
            openPosterModal={openPosterModal}
          />
          <TableContainer
            superModelsTableData={superModelsTable.data}
            isFetchingSM={isFetchingSM}
            tabViewIndex={tabViewIndex}
            columnSort={columnSort}
            setColumnSort={setColumnSort}
            itemsPerPage={itemsPerPage}
            selectedFilters={selectedFilters}
          />
          {!isFetchingSM && superModelsTable?.numberPages === 0 ? (
            <div>{t('no-result')}</div>
          ) : (
            <TablePagination
              numberPages={superModelsTable.numberPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
            />
          )}
        </div>
      )}
    </div>
  );
};
